import React from 'react';
import { useProducts } from '../context/ProductContext';
import { useQuote } from '../context/QuoteContext';
import { useSiteImages } from '../context/SiteImagesContext';
import { RoiCalculator } from '../components/RoiCalculator';
import { SafeImage } from '../components/SafeImage';
import { formatKsh } from '../utils/formatters';
import {
  ShieldCheck,
  CheckCircle,
  ArrowRight,
  Droplets,
  Layers,
  Wrench,
  Leaf,
  PhoneCall,
  ShoppingBag,
  Sparkles,
  MapPin,
  Clock,
  ExternalLink
} from 'lucide-react';

interface HomePageProps {
  onNavigate: (page: string, productId?: string) => void;
}

export const HomePage: React.FC<HomePageProps> = ({ onNavigate }) => {
  const { products } = useProducts();
  const { addToQuote, setIsDrawerOpen } = useQuote();
  const { images } = useSiteImages();

  const featuredProducts = products.slice(0, 5);

  return (
    <div className="space-y-20 pb-16">
      {/* Hero Section */}
      <section className="relative pt-6 sm:pt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-700">
                <span className="w-2 h-2 rounded-full bg-sky-600" />
                <span>Nairobi, Kenya & East Africa Fabrication</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.12]">
                Commercial Water Purification, Milk ATMs & Automated Vending Systems.
              </h1>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
                At Saints Tech Solutions, innovation meets reliability. We manufacture, install, and service food-grade Reverse Osmosis purifiers, precision Milk ATMs, water refilling stations, and salad oil kiosks engineered for maximum durability and profitability.
              </p>

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <button
                  onClick={() => onNavigate('products')}
                  className="px-6 py-3.5 bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-sm font-bold rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer"
                >
                  <span>Explore Machine Catalog</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setIsDrawerOpen(true)}
                  className="px-6 py-3.5 bg-white hover:bg-slate-50 text-slate-800 text-sm font-bold rounded-xl border border-slate-300 shadow-xs transition-colors cursor-pointer"
                >
                  Request Official Quote
                </button>
                <a
                  href="https://wa.me/254727072393"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 px-4 py-3.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                >
                  <span>WhatsApp: +254 727 072 393</span>
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>

              {/* Tagline callout */}
              <div className="pt-2 text-xs text-slate-500 italic font-medium flex items-center gap-2">
                <span className="text-rose-600 font-bold not-italic">Our Slogan:</span>
                "Purify life one drop at a time"
              </div>
            </div>

            {/* Right Hero Image Card */}
            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden bg-slate-900 shadow-xl border border-slate-200">
                <SafeImage
                  src={images.homeHero}
                  fallbackCategory="water-purification"
                  alt="Saints Tech Commercial Reverse Osmosis Water Purification Plant"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent flex flex-col justify-end p-6">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-sky-400">
                    Industrial Engineering
                  </span>
                  <h3 className="text-white text-lg font-bold">
                    Reverse Osmosis & Skid Water Treatment Plants
                  </h3>
                  <p className="text-slate-300 text-xs mt-1">
                    Custom built from 500L/hr to 10,000L/hr for boreholes, springs, and bottling lines.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 4-Column Proof & Trust Strip */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div className="p-4 sm:p-5 bg-white rounded-xl border border-slate-200 shadow-xs">
              <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center mb-3">
                <Layers className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900">Food-Grade SS 304</h4>
              <p className="text-xs text-slate-500 mt-1">
                All contact surfaces crafted from heavy-gauge stainless steel for maximum hygiene.
              </p>
            </div>

            <div className="p-4 sm:p-5 bg-white rounded-xl border border-slate-200 shadow-xs">
              <div className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900">KeBS Compliant</h4>
              <p className="text-xs text-slate-500 mt-1">
                Engineered to exceed national potable water & dairy equipment sanitary standards.
              </p>
            </div>

            <div className="p-4 sm:p-5 bg-white rounded-xl border border-slate-200 shadow-xs">
              <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3">
                <Sparkles className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900">GSM Audit Systems</h4>
              <p className="text-xs text-slate-500 mt-1">
                Real-time daily SMS sales reporting and tamper alerts straight to your phone.
              </p>
            </div>

            <div className="p-4 sm:p-5 bg-white rounded-xl border border-slate-200 shadow-xs">
              <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center mb-3">
                <Wrench className="w-5 h-5" />
              </div>
              <h4 className="text-sm font-bold text-slate-900">Field Technicians</h4>
              <p className="text-xs text-slate-500 mt-1">
                Dedicated workshop at Juja Road Green Mall with on-site dispatch nationwide.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products Section (Matching Uploaded Screenshot Products) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-sky-600 mb-1">
              Top Commercial Equipment
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Featured Machines & Dispensers
            </h2>
            <p className="text-slate-600 text-sm mt-1">
              Direct factory pricing in Kenyan Shillings with full manufacturer warranty.
            </p>
          </div>
          <button
            onClick={() => onNavigate('products')}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-sky-700 hover:text-sky-800 transition-colors"
          >
            <span>View All Equipment ({products.length})</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between group"
            >
              <div>
                {/* Image Frame */}
                <div className="relative aspect-4/3 bg-slate-100 overflow-hidden">
                  <SafeImage
                    src={product.image}
                    fallbackCategory={product.category}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-300"
                  />
                  <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-xs px-2.5 py-1 rounded-md text-[11px] font-bold text-slate-800 shadow-xs">
                    {product.capacity}
                  </div>
                  {product.inStock && (
                    <div className="absolute top-3 right-3 bg-emerald-600 text-white px-2 py-0.5 rounded text-[10px] font-bold">
                      In Stock
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="p-5 space-y-3">
                  <h3 className="text-base font-bold text-slate-900 line-clamp-2 leading-snug group-hover:text-sky-700 transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                    {product.tagline}
                  </p>

                  <div className="flex items-baseline gap-2 pt-1">
                    <span className="text-xl font-extrabold text-slate-900 tabular-nums">
                      KSh {formatKsh(product.priceKsh)}
                    </span>
                    {product.originalPriceKsh && (
                      <span className="text-xs text-slate-400 line-through tabular-nums">
                        KSh {formatKsh(product.originalPriceKsh)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-5 pt-0 border-t border-slate-100 flex items-center gap-2 mt-2">
                <a
                  href={`/product/${product.id}`}
                  onClick={(e) => { e.preventDefault(); onNavigate('product-detail', product.id); }}
                  className="flex-1 py-2 px-3 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer text-center block"
                >
                  View Machine Page
                </a>
                <button
                  onClick={() => addToQuote(product, 1)}
                  className="py-2 px-3 text-xs font-semibold text-white bg-sky-600 hover:bg-sky-700 rounded-lg transition-colors flex items-center gap-1.5 cursor-pointer"
                  title="Add to quotation basket"
                >
                  <ShoppingBag className="w-3.5 h-3.5" />
                  <span>Add Quote</span>
                </button>
              </div>
            </div>
          ))}

          {/* Quick Custom Order card */}
          <div className="bg-gradient-to-br from-slate-900 to-sky-950 rounded-2xl p-6 text-white flex flex-col justify-between border border-slate-800">
            <div className="space-y-3">
              <span className="text-xs uppercase tracking-wider text-sky-400 font-bold">
                Tailored Fabrication
              </span>
              <h3 className="text-xl font-bold leading-tight">
                Need a Custom Capacity or Borehole Skid?
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                We custom-build high-volume Reverse Osmosis filtration systems (2,000L/hr to 20,000L/hr), multibank pasteurizers, and solar-powered milk ATMs tailored to your local site requirements.
              </p>
            </div>
            <div className="pt-6">
              <a
                href="tel:+254727072393"
                className="w-full py-3 px-4 bg-sky-500 hover:bg-sky-400 text-slate-950 text-xs font-bold rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                <PhoneCall className="w-4 h-4" />
                Speak to Technical Sales Engineer
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Business ROI Calculator Preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-8 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            Cashflow Modeling
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            How Much Can You Earn With a Saints Tech ATM?
          </h2>
          <p className="text-sm text-slate-600">
            Calculate daily profit margins and see how fast your equipment pays for itself in the Kenyan market.
          </p>
        </div>

        <RoiCalculator />
      </section>

      {/* Why Choose Saints Tech Solutions */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-100 rounded-3xl p-8 sm:p-12 border border-slate-200">
          <div className="max-w-3xl mb-10">
            <div className="text-xs font-bold uppercase tracking-wider text-sky-700 mb-1">
              Core Pillars
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Why Kenyan Businesses Choose Saints Tech Solution
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              From small town startups to leading dairy cooperatives, our machines deliver exceptional reliability and uninterrupted uptime.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-3">
              <div className="w-10 h-10 rounded-xl bg-sky-600 text-white flex items-center justify-center font-bold text-sm">
                01
              </div>
              <h3 className="text-lg font-bold text-slate-900">Innovation & Precision</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                We leverage the latest technologies to develop products that are not only efficient but also ahead of the curve in terms of functionality and design. Automated GSM tracking, digital flowmeters, and precision PID heaters keep you in control.
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
                02
              </div>
              <h3 className="text-lg font-bold text-slate-900">Sustainability First</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                We are dedicated to creating solutions that are environmentally friendly and sustainable. Our refill vending kiosks eliminate single-use plastics and packaging waste, while our low-power chillers minimize energy consumption.
              </p>
            </div>

            <div className="space-y-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-sm">
                03
              </div>
              <h3 className="text-lg font-bold text-slate-900">Rigorous Quality Testing</h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                Our products undergo rigorous pressure, hydraulic, and microbiological testing before leaving our Juja Road workshop, ensuring they meet the highest standards of durability, safety, and sanitary compliance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Servicing & Spare Parts Spotlight */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-white rounded-2xl border border-slate-200 p-8 sm:p-10 shadow-xs">
          <div className="lg:col-span-7 space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-rose-600">
              <Wrench className="w-4 h-4" />
              <span>Full After-Sales Support & Spare Parts</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
              Comprehensive Servicing and Readily Available Spare Parts
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              At Saints Tech Solution, we understand that maintaining the efficiency and longevity of your equipment is crucial. That’s why we stock a complete inventory of dosing pumps, RO membranes, flow sensors, food-grade valves, and electronic control boards.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Mobile technician callouts in Nairobi & counties</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>RO membrane chemical washing & rejuvenation</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Flowmeter calibration & GSM audit upgrades</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-700">
                <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Borehole water physical & chemical lab testing</span>
              </div>
            </div>

            <div className="pt-4 flex flex-wrap items-center gap-3">
              <button
                onClick={() => onNavigate('services')}
                className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
              >
                View Services & Parts Catalog
              </button>
              <a
                href="tel:+254727072393"
                className="px-5 py-2.5 bg-rose-50 text-rose-700 hover:bg-rose-100 text-xs font-bold rounded-lg transition-colors"
              >
                Emergency Technician: +254 727 072 393
              </a>
            </div>
          </div>

          <div className="lg:col-span-5 bg-slate-50 p-6 rounded-xl border border-slate-200 space-y-4">
            <h4 className="text-xs uppercase tracking-wider text-slate-700 font-bold">
              In-Stock Spare Components
            </h4>
            <div className="space-y-2 text-xs text-slate-600">
              <div className="flex justify-between py-1.5 border-b border-slate-200">
                <span className="font-medium text-slate-800">Dow Filmtec 4040 RO Membrane</span>
                <span className="font-bold text-sky-700 tabular-nums">KSh 18,500</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-200">
                <span className="font-medium text-slate-800">Digital Chemical Dosing Pump</span>
                <span className="font-bold text-sky-700 tabular-nums">KSh 32,000</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-200">
                <span className="font-medium text-slate-800">55W Inline UV Disinfection Chamber</span>
                <span className="font-bold text-sky-700 tabular-nums">KSh 38,000</span>
              </div>
              <div className="flex justify-between py-1.5 border-b border-slate-200">
                <span className="font-medium text-slate-800">Smart Vending PLC + GSM Board</span>
                <span className="font-bold text-sky-700 tabular-nums">KSh 22,000</span>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">
              Direct counter pickup at Juja Road Green Mall, Nairobi, or express courier delivery across Kenya.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials / Real Client Feedback */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-xl mx-auto mb-10 space-y-1">
          <div className="text-xs font-bold uppercase tracking-wider text-sky-600">
            Trusted By Entrepreneurs
          </div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
            Proven Performance on the Ground
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
            <p className="text-xs text-slate-600 leading-relaxed italic">
              "We purchased two 100L Milk ATMs for our dairy shop in Kasarani. The GSM sales summary sends an SMS every evening with the exact liters sold, eliminating pilferage. Milk stays ice cold throughout."
            </p>
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-bold text-slate-900">Peter K. Njoroge</div>
              <div className="text-[11px] text-slate-500">Kasarani Fresh Dairy Hub, Nairobi</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
            <p className="text-xs text-slate-600 leading-relaxed italic">
              "Our 500 Litres/hr Reverse Osmosis plant has been running for 14 months purifying borehole water. TDS dropped from 940 to 32. KeBS certified our brand smoothly thanks to Saints Tech."
            </p>
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-bold text-slate-900">Grace Wambui</div>
              <div className="text-[11px] text-slate-500">PureStream Bottlers, Ruiru</div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 space-y-3 shadow-xs">
            <p className="text-xs text-slate-600 leading-relaxed italic">
              "The 300L 3-phase pasteurizer has transformed our cooperative processing. It reaches 75°C quickly with the water jacket, and the agitator ensures even heat distribution with zero scorching."
            </p>
            <div className="pt-2 border-t border-slate-100">
              <div className="text-xs font-bold text-slate-900">Daniel Koech</div>
              <div className="text-[11px] text-slate-500">Highland Dairy Farmers Co-op, Eldoret</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Location & Office Visit Card */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 border border-slate-800">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="space-y-4">
              <span className="text-xs uppercase tracking-wider text-sky-400 font-bold">
                Visit Our Workshop & Showroom
              </span>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                Juja Road 478/B Green Mall, Nairobi
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed">
                Inspect machines in person, observe live water testing demonstrations, and discuss tailored configurations with our fabricators.
              </p>
              <div className="space-y-2 text-xs text-slate-300 pt-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <span>Monday - Friday: 9:00 AM – 5:00 PM (Weekend: Closed)</span>
                </div>
                <div className="flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-emerald-400" />
                  <span className="tabular-nums">+254 727 072 393 / info@saintstech.co.ke</span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <button
                onClick={() => onNavigate('contact')}
                className="px-6 py-3.5 bg-sky-600 hover:bg-sky-500 text-white text-xs font-bold rounded-xl shadow-sm transition-colors text-center cursor-pointer"
              >
                Contact & Route Directions
              </button>
              <a
                href="https://wa.me/254727072393?text=Hello%20Saints%20Tech%20Solutions,%20I%20would%20like%20to%20visit%20your%20Juja%20Road%20office."
                target="_blank"
                rel="noreferrer"
                className="px-6 py-3.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl shadow-sm transition-colors text-center"
              >
                Schedule Appointment on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
